import {Component, ElementRef, OnInit, AfterViewInit, ViewChild, OnDestroy} from '@angular/core';
import {UserProfileService} from '../../services/user/user-profile.service';
import {debounceTime, distinctUntilChanged, filter, tap} from 'rxjs/operators';
import {BehaviorSubject, fromEvent, Subject, Subscription} from 'rxjs';
import {InternationalizationService} from "../../services/internationalization/internationalization.service";
import {$translate} from "../../services/internationalization/localize.service";
import {FilteringEventArgs} from "@syncfusion/ej2-dropdowns/src/drop-down-base/drop-down-base";

interface TenancyOption {
  active: boolean;
  depth: number;
  displayValue: string;
  filterID: string;
  parentID?: string;
  visible: boolean;
}

@Component({
  selector: 'sb-tenancy-selection',
  templateUrl: './tenancy-selection.component.html',
  styleUrls: ['./tenancy-selection.component.scss'],
})
export class TenancySelectionComponent implements OnInit, OnDestroy {
  public selectedOption: TenancyOption;
  public filteredOptions: TenancyOption[];

  public searchPlaceholder: string;
  private localeChanged$: Subscription;
  private onFiltering$ = new Subject<{text: string, event: FilteringEventArgs}>();

  constructor(
    private userProfileService: UserProfileService,
    private i18nService: InternationalizationService
  ) {}

  async ngOnInit() {
    this.filteredOptions = await this.getFilteredOptions();
    this.selectedOption = this.filteredOptions.find(it => it.active) || null;

    this.onFiltering$.pipe(
      debounceTime(250),
      tap(async (x: {text: string, event: FilteringEventArgs}) => {
        const newFilteredOptions = await this.getFilteredOptions(x.text);
        x.event.updateData(newFilteredOptions);
      }),
    )
    .subscribe();

    this.localeChanged$ = this.i18nService.localeChanged$
    .subscribe((locale: string) => this.updateMessages());

    this.updateMessages();
  }

  ngOnDestroy(): void {
    this.localeChanged$ && this.localeChanged$.unsubscribe();
    this.onFiltering$ && this.onFiltering$.unsubscribe();
  }

  private async getFilteredOptions(text: string = '') {
    return (await this.userProfileService.getTenantFilteringOptions(text))
    .map(item => ({ ...item, visible: true }));
  }

  private updateMessages() {
    this.searchPlaceholder = $translate("ng.component.tenancy-selection.placeholder")`Search`;
  }

  public indent(level) {
    return '&nbsp;&nbsp;'.repeat(level);
  }

  onFiltering($event: FilteringEventArgs) {
    this.onFiltering$.next({text: $event.text, event: $event});
  }

  onChange($event) {
    this.selectedOption = $event.itemData;
    this.setTenantFilteringOptions($event.itemData);
  }

  private async setTenantFilteringOptions(option: TenancyOption) {
    this.filteredOptions = await this.userProfileService.setTenantFilteringOptions(option);
    this.updateMessages();
    const MessageBus = (window as any).MessageBus;
    MessageBus.publish(MessageBus.CHANNELS.REFRESH_EVENT, "TENANCY_FILTER_CHANGE");
  }

}
